import React, { useState, useEffect } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths } from 'date-fns';

interface Post {
  id: string;
  scheduledDate: string;
  scheduledTime: string;
  platforms: string[];
  status: 'draft' | 'approved' | 'scheduled' | 'publishing' | 'published' | 'failed';
  content: {
    topic: string;
    caption: string;
    hashtags: string[];
  };
  strategy: {
    contentPillar: string;
    contentType: string;
    goal: string;
    productCategory: string;
  };
  assets?: any;
}

interface BlogSchedule {
  id: string;
  scheduledDate: string;
  topic: string;
  contentPillar: string;
  keywords: string[];
  productCategories: string[];
  status: 'draft' | 'generated' | 'uploaded' | 'scheduled' | 'published' | 'failed';
}



const PostingCalendar: React.FC = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [posts, setPosts] = useState<Post[]>([]);
  const [blogs, setBlogs] = useState<BlogSchedule[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [view, setView] = useState<'calendar' | 'list'>('calendar');
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    loadCalendarData();
  }, [currentMonth]);

  const loadCalendarData = async () => {
    setLoading(true);
    try {
      const start = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
      const end = format(endOfMonth(currentMonth), 'yyyy-MM-dd');

      const [postsRes, blogsRes] = await Promise.all([
        fetch(`/api/calendar/posts?startDate=${start}&endDate=${end}`, {
          credentials: 'include'
        }),
        fetch(`/api/calendar/blogs?startDate=${start}&endDate=${end}`, {
          credentials: 'include'
        })
      ]);

      const postsData = await postsRes.json();
      const blogsData = await blogsRes.json();

      if (postsData.success) setPosts(postsData.posts);
      if (blogsData.success) setBlogs(blogsData.blogs);
    } catch (error) {
      console.error('Error loading calendar data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateStrategy = async () => {
    if (!confirm('Generate a 30-day content strategy? This will create posts and blog schedules.')) {
      return;
    }

    setGenerating(true);
    try {
      const response = await fetch('/api/calendar/generate-strategy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          days: 30,
          platforms: ['pinterest', 'instagram', 'twitter', 'linkedin'],
          includeBlogs: true,
          blogsPerWeek: 2
        })
      });

      const data = await response.json();

      if (data.success) {
        alert(`Strategy generated!\n${data.summary.totalPosts} posts\n${data.summary.totalBlogs} blogs`);
        loadCalendarData();
      } else {
        alert('Error generating strategy: ' + data.error);
      }
    } catch (error) {
      console.error('Error generating strategy:', error);
      alert('Failed to generate strategy');
    } finally {
      setGenerating(false);
    }
  };

  const generateTrendBasedCalendar = async (days: number) => {
    if (!confirm(`Generate a ${days}-day content calendar based on your TrendLab data? This will analyze trends, insights, and keywords to create optimized content recommendations.`)) {
      return;
    }

    setGenerating(true);
    try {
      const response = await fetch('/api/calendar/generate-trend-calendar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          days,
          platforms: ['pinterest', 'instagram', 'twitter', 'linkedin']
        })
      });

      const data = await response.json();

      if (data.success) {
        alert(`Trend-based calendar generated!\n${data.summary.totalPosts} posts created\n\nTrends analyzed: ${data.summary.trendsAnalyzed}\nInsights used: ${data.summary.insightsUsed}\nKeywords incorporated: ${data.summary.keywordsIncorporated}\n\nTop trends: ${data.trendInsights?.topTrends?.slice(0, 3).join(', ') || 'None found'}`);
        loadCalendarData();
      } else {
        alert('Error generating trend calendar: ' + data.error);
      }
    } catch (error) {
      console.error('Error generating trend calendar:', error);
      alert('Failed to generate trend-based calendar');
    } finally {
      setGenerating(false);
    }
  };

  const createAssetsForDateRange = async (days: number) => {
    if (!confirm(`Create assets for the next ${days} days? This may take several minutes.`)) {
      return;
    }

    setGenerating(true);
    try {
      const startDate = format(new Date(), 'yyyy-MM-dd');
      const endDate = format(new Date(Date.now() + days * 24 * 60 * 60 * 1000), 'yyyy-MM-dd');

      const response = await fetch('/api/calendar/create-assets/range', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ startDate, endDate })
      });

      const data = await response.json();

      if (data.success) {
        alert(`Assets created!\nSuccessful: ${data.summary.successful}\nFailed: ${data.summary.failed}`);
        loadCalendarData();
      } else {
        alert('Error creating assets: ' + data.error);
      }
    } catch (error) {
      console.error('Error creating assets:', error);
      alert('Failed to create assets');
    } finally {
      setGenerating(false);
    }
  };

  const getPostsForDate = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return posts.filter(p => p.scheduledDate === dateStr);
  };

  const getBlogsForDate = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return blogs.filter(b => b.scheduledDate === dateStr);
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      draft: 'bg-muted/50 text-muted-foreground',
      approved: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
      scheduled: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
      publishing: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400',
      published: 'bg-green-500/10 text-green-600 dark:text-green-400',
      failed: 'bg-red-500/10 text-red-600 dark:text-red-400',
      generated: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
      uploaded: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400'
    };
    return colors[status] || 'bg-muted/50 text-muted-foreground';
  };

  const getPlatformIcon = (platform: string) => {
    const icons: Record<string, string> = {
      pinterest: '📌',
      instagram: '📷',
      twitter: '🐦',
      linkedin: '💼'
    };
    return icons[platform] || '📱';
  };

  const renderCalendarView = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

    return (
      <div className="bg-card rounded-lg shadow p-6 border border-border">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
              className="px-3 py-1 bg-secondary text-secondary-foreground rounded hover:bg-secondary/80"
            >
              ←
            </button>
            <h2 className="text-2xl font-bold text-foreground">
              {format(currentMonth, 'MMMM yyyy')}
            </h2>
            <button
              onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
              className="px-3 py-1 bg-secondary text-secondary-foreground rounded hover:bg-secondary/80"
            >
              →
            </button>
          </div>
          <button
            onClick={() => setCurrentMonth(new Date())}
            className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90"
          >
            Today
          </button>
        </div>

        <div className="grid grid-cols-7 gap-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="text-center font-semibold text-muted-foreground py-2">
              {day}
            </div>
          ))}

          {days.map(day => {
            const dayPosts = getPostsForDate(day);
            const dayBlogs = getBlogsForDate(day);
            const isToday = isSameDay(day, new Date());
            const isSelected = selectedDate && isSameDay(day, selectedDate);

            return (
              <div
                key={day.toISOString()}
                onClick={() => setSelectedDate(day)}
                className={`min-h-[120px] p-2 border rounded cursor-pointer transition-colors ${isToday ? 'bg-primary/5 border-primary' : 'border-border bg-card'
                  } ${isSelected ? 'ring-2 ring-primary' : ''} hover:bg-muted/50`}
              >
                <div className="text-sm font-semibold mb-1 text-foreground">
                  {format(day, 'd')}
                </div>

                <div className="space-y-1">
                  {dayPosts.slice(0, 3).map(post => (
                    <div
                      key={post.id}
                      className={`text-xs px-1 py-0.5 rounded truncate ${getStatusColor(post.status)}`}
                      title={post.content.topic}
                    >
                      {post.platforms.map(p => getPlatformIcon(p)).join(' ')} {post.content.topic.substring(0, 20)}
                    </div>
                  ))}

                  {dayBlogs.map(blog => (
                    <div
                      key={blog.id}
                      className={`text-xs px-1 py-0.5 rounded truncate ${getStatusColor(blog.status)}`}
                      title={blog.topic}
                    >
                      📝 {blog.topic.substring(0, 20)}
                    </div>
                  ))}

                  {dayPosts.length > 3 && (
                    <div className="text-xs text-muted-foreground">
                      +{dayPosts.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderListView = () => {
    return (
      <div className="bg-card rounded-lg shadow p-6 border border-border">
        <h2 className="text-2xl font-bold mb-4 text-foreground">All Scheduled Content</h2>

        <div className="space-y-6">
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
                Social Media Posts <span className="text-xs px-2 py-1 bg-primary/20 text-primary rounded-full">Coming Soon</span>
              </h3>
            </div>
            {/* Blurred/Disabled Social Media List */}
            <div className="relative">
              <div className="absolute inset-0 bg-background/60 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-lg border border-dashed border-border">
                <div className="text-center p-6 bg-card/80 rounded-xl shadow-sm border border-border">
                  <p className="font-semibold text-foreground">Social Media Integration In Development</p>
                  <p className="text-sm text-muted-foreground">Scheduing for Instagram, Pinterest, and X will be available soon.</p>
                </div>
              </div>
              <div className="space-y-2 opacity-50 pointer-events-none select-none filter blur-[2px]">
                {/* Mock items to show 'content' behind blur if empty, or actual posts if they exist */}
                {(posts.length > 0 ? posts : [1, 2, 3]).map((post: any, i) => (
                  <div key={i} className="border border-border rounded p-4 bg-card">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-semibold text-foreground">Sample Social Post {i + 1}</span>
                          <span className="text-xs px-2 py-1 rounded bg-muted text-muted-foreground">Draft</span>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          📱 Instagram • Scheduled for later
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-xl font-semibold mb-3 text-foreground">Blog Posts ({blogs.length})</h3>
            <div className="space-y-2">
              {blogs.map(blog => (
                <div key={blog.id} className="border border-border rounded p-4 hover:bg-muted/50 bg-card">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-semibold text-foreground">{blog.topic}</span>
                        <span className={`text-xs px-2 py-1 rounded ${getStatusColor(blog.status)}`}>
                          {blog.status}
                        </span>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        📝 {format(new Date(blog.scheduledDate), 'MMM d, yyyy')}
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">
                        {blog.contentPillar} • {blog.keywords.join(', ')}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-4 text-foreground">📅 Posting Calendar</h1>

        <div className="flex flex-wrap gap-3 mb-4">
          <button
            onClick={generateStrategy}
            disabled={generating}
            className="px-4 py-2 bg-gradient-to-r from-primary to-primary/80 text-primary-foreground rounded hover:opacity-90 disabled:opacity-50"
          >
            {generating ? '⏳ Generating...' : '✨ Generate AI Strategy'}
          </button>

          <button
            onClick={() => generateTrendBasedCalendar(14)}
            disabled={generating}
            className="px-4 py-2 bg-secondary text-secondary-foreground rounded hover:bg-secondary/80 disabled:opacity-50"
          >
            {generating ? '⏳ Generating...' : '📈 Generate Trend Calendar'}
          </button>

          <button
            onClick={() => createAssetsForDateRange(7)}
            disabled={generating}
            className="px-4 py-2 bg-accent text-accent-foreground border border-border rounded hover:bg-accent/80 disabled:opacity-50"
          >
            🎨 Create Assets (Next 7 Days)
          </button>

          <button
            onClick={() => createAssetsForDateRange(14)}
            disabled={generating}
            className="px-4 py-2 bg-accent text-accent-foreground border border-border rounded hover:bg-accent/80 disabled:opacity-50"
          >
            🎨 Create Assets (Next 14 Days)
          </button>

          <button
            onClick={() => setView(view === 'calendar' ? 'list' : 'calendar')}
            className="px-4 py-2 bg-muted text-foreground border border-border rounded hover:bg-muted/80"
          >
            {view === 'calendar' ? '📋 List View' : '📅 Calendar View'}
          </button>

          <button
            onClick={loadCalendarData}
            className="px-4 py-2 bg-muted text-foreground border border-border rounded hover:bg-muted/80"
          >
            🔄 Refresh
          </button>
        </div>

        <div className="flex gap-4 text-sm flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-muted/50 rounded"></div>
            <span className="text-muted-foreground">Draft</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-blue-500/10 rounded border border-blue-500/20"></div>
            <span className="text-muted-foreground">Approved</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-purple-500/10 rounded border border-purple-500/20"></div>
            <span className="text-muted-foreground">Scheduled</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-green-500/10 rounded border border-green-500/20"></div>
            <span className="text-muted-foreground">Published</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-red-500/10 rounded border border-red-500/20"></div>
            <span className="text-muted-foreground">Failed</span>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="text-xl">Loading calendar...</div>
        </div>
      ) : (
        view === 'calendar' ? renderCalendarView() : renderListView()
      )}
    </div>
  );
};

export default PostingCalendar;
